import { useQuery } from '@tanstack/react-query';
import { getMe, listMembers } from './api';

/**
 * What the viewer's role on a project lets them press.
 *
 * Every shared-project surface was written as though the row's creator were the only person who
 * would ever read it, so a reporter got Complete, Edit and "Take it on" as ordinary black buttons.
 * Pressing any of them produced *nothing*: the server answered 403 with a perfectly clear message
 * and the UI dropped it. A refusal you cannot see and a broken app are the same experience.
 *
 * The server remains the authority — this decides only whether to *offer* a control, and what to
 * say when it does not. It is a mirror of `roleAllows` in `packages/core/src/auth/canAccess.ts`,
 * kept here rather than imported because the web bundle does not depend on core (which pulls in
 * `pg` and `drizzle`). `roleLadderMirrorsCore.spec.ts` compares the two tables entry by entry, so
 * the copy cannot drift silently — which is the only real cost of duplicating it.
 */
export const ACCESS_LEVELS = ['read', 'assign', 'write', 'admin', 'own'] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];

/** Weakest to strongest, matching `PROJECT_ROLES`. */
const ROLE_RANK: Record<string, number> = {
  reporter: 0,
  developer: 1,
  maintainer: 2,
  owner: 3,
};

/** The weakest role that passes each level. */
const LEVEL_NEEDS: Record<AccessLevel, number> = {
  read: 0,
  assign: 1,
  write: 2,
  admin: 2,
  own: 3,
};

/**
 * `null` — no membership, or one still loading — is false rather than true: briefly missing a
 * control is a smaller wrong than briefly offering one that fails.
 */
export function roleAllows(role: string | null | undefined, level: AccessLevel): boolean {
  if (!role) return false;
  const rank = ROLE_RANK[role];
  return rank !== undefined && rank >= LEVEL_NEEDS[level];
}

/**
 * Whether a role may take work on. Claiming is `assign`, which is developer and above.
 *
 * Split out so it can be tested without a browser and two signed-in users.
 */
export function roleCanClaim(role: string | null): boolean {
  return roleAllows(role, 'assign');
}

/**
 * What each role may do instead, in the second person.
 *
 * A `disabled` attribute on its own is a quieter silence than the one this fixes — the reader
 * still cannot tell whether they mis-clicked, whether the app is broken, or whether they are
 * simply not allowed. So the reason is written where the control is.
 *
 * Reporters *can* comment: the gate dropped from `assign` to `read` in v1.11.0, and this sentence
 * is the one place the UI states a reporter's positive rights, so it says so rather than implying
 * they are mute.
 *
 * The developer's sentence said "you can progress and file work". *File* was meant as filing under
 * labels and goals, but this repo also says "file work into a project" for creating a task, which a
 * developer may not do (`createTask` asks `write` of the project) — and the sentence is printed
 * beside the Projects screen's disabled "Add a task to this project", where it read as the
 * opposite of the greyed box under it. It names the act now: label, which since Phase 107 includes
 * taking one off, on any task they can reach. "Progress" stays without a hold clause; a refused
 * status change names the hold itself (`cd44020`), and claiming is the first thing listed.
 */
const INSTEAD: Record<string, string> = {
  reporter: 'you can read and comment, not change the work',
  developer: 'you can take work on, progress it and label it, not create it or change what it is',
  maintainer: 'that one is the owner’s',
};

/**
 * Why this level is refused, or null when it is not refused — and also null while the membership
 * is still loading, because a sentence naming the wrong role is worse than no sentence.
 */
export function roleReason(role: string | null, level: AccessLevel): string | null {
  if (roleAllows(role, level)) return null;
  if (!role) return null;
  const instead = INSTEAD[role];
  return instead
    ? `Your role on this project is ${role} — ${instead}.`
    : `Your role on this project does not allow that.`;
}

/**
 * The viewer's own role on one project, and the two questions asked of it.
 *
 * Shares React Query's `['members', id]` cache with `useAssignees`, so a screen showing both a
 * list and a detail asks once. That cache is a minute stale by design, which is exactly why the
 * screens that use this also print the server's 403 when one arrives: a role can change under an
 * open page, and then the offer is wrong however carefully it was decided.
 */
export function useProjectRole(projectId: string | null) {
  const me = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false });
  const members = useQuery({
    queryKey: ['members', projectId],
    queryFn: () => listMembers(projectId!),
    enabled: projectId !== null,
    staleTime: 60_000,
  });

  /* Accepted, or it is not a membership — the same rule `authorizeProject` joins on. */
  const mine = (members.data ?? []).find((m) => m.userId === me.data?.id && m.acceptedAt !== null);
  const role = projectId === null ? null : (mine?.role ?? null);
  const known = projectId === null || (members.isFetched && me.isFetched);

  return {
    /** Null outside a project, and null until the membership has arrived. */
    role,
    /** Outside a project there is no role gate at all: it is your own work. */
    can: (level: AccessLevel) => projectId === null || roleAllows(role, level),
    /** The sentence to print beside a control this role cannot use. */
    why: (level: AccessLevel) => (known ? roleReason(role, level) : null),
  };
}
